import {
    Button,
    createStyles,
    Dialog,
    Grid,
    Menu,
    MenuItem,
    Switch,
    SwitchClassKey,
    SwitchProps,
    Theme,
    withStyles
} from "@material-ui/core";
import { FormikErrors, FormikTouched } from "formik";
import { toPairs, uniq } from "lodash";
import { observer } from "mobx-react";
import moment, { Moment } from "moment";
import * as React from "react";
import styled from "styled-components";
import { useOverflow } from "use-overflow";
import { v4 as uuidv4 } from "uuid";
import { NAVBAR_HEIGHT } from "../../config";
import { allStatusOptions, getJobCategoryOptions, Option } from "../../constants/index";
import { t } from "../../i18n/util";
import {
    GetApplicantDetailsResponseHistory,
    GetApplicantsObjectInterviewDates,
    NoteComment,
    User
} from "../../network/APITypes";
import { isDal, isDarkMode, THEME } from "../../theme/ThemeStore";
import { CustomColors, IApplicationStateEnum, JobCategoryEnum } from "../../types/models";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { TableSelection } from "../hooks/useTableSelection";
import {
    getSortedTranslatedOptions,
    getTranslatedOptions,
    historyToString,
    jobCategoryToColors,
    optionToString
} from "../util/helpers";
import { ButtonContainer } from "./ButtonContainer";
import { CustomCheckbox } from "./CustomCheckbox";

type HrBotButtonProps = {
    disabled?: boolean;
    label: string;
    type?: "button" | "submit" | "reset";
    onClick?: () => void;
    style?: React.CSSProperties;
    small?: boolean;
};

export const HrBotButton = ({ disabled, label, type, onClick, style, small }: HrBotButtonProps) => {
    const isMobile = useMobileMediaQuery();

    return (
        <Button
            variant="contained"
            color="primary"
            disabled={disabled}
            onClick={onClick}
            type={type}
            style={{
                color: THEME.customColors.white,
                alignSelf: "center",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                width: isMobile ? "100%" : "auto",
                padding: small ? "8px 16px" : "18px 26px",
                fontSize: small ? 12 : 14,
                height: small ? 58 : "unset",
                borderRadius: THEME.THEME_NAME === "dal" ? 100 : undefined,
                ...style
            }}
        >
            {label}
        </Button>
    );
};

type HrBotOutlinedButtonProps = {
    disabled?: boolean;
    label: string;
    type?: "button" | "submit" | "reset";
    onClick?: () => void;
    style?: React.CSSProperties;
    small?: boolean;
};

export const HrBotOutlinedButton = ({ disabled, label, type, onClick, style, small }: HrBotOutlinedButtonProps) => {
    const isMobile = useMobileMediaQuery();

    return (
        <Button
            variant="contained"
            color="primary"
            disabled={disabled}
            onClick={onClick}
            type={type}
            style={{
                color: THEME.customColors.primary,
                backgroundColor: THEME.customColors.darkPrimary,
                border: `2px solid ${THEME.customColors.primary}`,
                alignSelf: "center",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                width: isMobile ? "100%" : "auto",
                padding: small ? "6px 14px" : "16px 24px",
                fontSize: small ? 12 : 14,
                height: small ? 58 : "unset",
                ...style
            }}
        >
            {label}
        </Button>
    );
};

type HrBotBackButtonProps = {
    label: string | React.ReactNode;
    onClick?: () => void;
    style?: React.CSSProperties;
};

export const HrBotBackButton = ({ label, onClick, style }: HrBotBackButtonProps) => (
    <Button
        onClick={onClick}
        style={{
            color: THEME.customColors.primary,
            fontWeight: 600,
            padding: "18px 26px",
            fontSize: 14,
            ...style
        }}
    >
        {label}
    </Button>
);

type HrBotErrorMessageProps = { label?: string };

export const HrBotErrorMessage = ({ label }: HrBotErrorMessageProps) => (
    <div
        style={{
            marginBottom: 16,
            color: THEME.customColors.error,
            fontSize: 12,
            fontWeight: 400
        }}
    >
        {label}
    </div>
);

type FileOverviewFieldProps = { name: string; data: File };

export const FileOverviewField = ({ name, data }: FileOverviewFieldProps) => {
    const handleClick = () => {
        const objectURL = URL.createObjectURL(data);
        window.open(objectURL);
    };

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                marginTop: 10,
                marginBottom: 10,
                cursor: "pointer",
                gap: 10,
                color: THEME.customColors.headingsDark,
                wordBreak: "break-word"
            }}
            className="caption"
            onClick={handleClick}
        >
            <img src={THEME.Images.icFileBlack} alt="" />
            {name}
        </div>
    );
};

type LinkOverviewFieldProps = { name: string };

export const LinkOverviewField = ({ name }: LinkOverviewFieldProps) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            marginTop: 10,
            marginBottom: 10,
            gap: 10,
            color: THEME.customColors.headingsDark,
            wordBreak: "break-word"
        }}
        className="caption"
    >
        <img src={THEME.Images.icLink} alt="" />
        {name}
    </div>
);

type ErrorMessagesListProps = {
    errors: FormikErrors<any>;
    touched: FormikTouched<any>;
    style?: React.CSSProperties;
};

export const ErrorMessagesList = ({ errors, touched, style }: ErrorMessagesListProps) => (
    <div style={{ marginTop: 6, ...style }}>
        {uniq(toPairs(errors).map((error) => touched[error[0]] && error[1])).map(
            (error) => error && <HrBotErrorMessage key={uuidv4()} label={error as string} />
        )}
    </div>
);

type SectionHeaderSmallProps = { label?: string; style?: React.CSSProperties };

export const SectionHeaderSmall = ({ label, style }: SectionHeaderSmallProps) => (
    <div className="body1semibold" style={{ color: THEME.customColors.headingsDark, ...style }}>
        {label}
    </div>
);

type FormButtonsProps = {
    labelBack: string;
    labelSubmit: string;
    onBack?: () => void;
    onSubmit?: () => void;
    middleButtons?: boolean;
    style?: React.CSSProperties;
};

export const FormButtons = ({ labelBack, labelSubmit, onBack, onSubmit, middleButtons, style }: FormButtonsProps) => {
    const isMobile = useMobileMediaQuery();

    return (
        <ButtonContainer
            style={{
                ...style,
                pointerEvents: "none"
            }}
        >
            {middleButtons ? (
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-end"
                    }}
                >
                    <HrBotBackButton
                        onClick={onBack}
                        label={labelBack}
                        style={{ pointerEvents: "auto", marginRight: isMobile ? 0 : 64 }}
                    />
                </div>
            ) : (
                <HrBotBackButton
                    label={labelBack}
                    onClick={onBack}
                    style={{ pointerEvents: "auto", marginRight: isMobile ? 0 : 30 }}
                />
            )}
            <HrBotButton label={labelSubmit} type="submit" onClick={onSubmit} style={{ pointerEvents: "auto" }} />
            {middleButtons && THEME.THEME_NAME !== "dal" && <div style={{ flex: 1 }} />}
        </ButtonContainer>
    );
};

type TextPreviewProps = { children: React.ReactNode; style?: React.CSSProperties; showButton?: boolean };

export const TextPreview = ({ children, style, showButton }: TextPreviewProps) => {
    const [isPreview, setIsPreview] = React.useState(true);

    const textPreviewRef = React.useRef<HTMLDivElement | null>(null);
    const { refYOverflowing } = useOverflow(textPreviewRef);

    return (
        <div
            onClick={() => {
                if (!showButton) {
                    setIsPreview(!isPreview);
                }
            }}
            style={{
                cursor: showButton || !refYOverflowing ? "default" : "pointer",
                ...style
            }}
        >
            <p
                style={{
                    maxWidth: "100%",
                    display: isPreview ? "-webkit-box" : "inline",
                    WebkitBoxOrient: isPreview ? "vertical" : "inline-axis",
                    WebkitLineClamp: isPreview ? 3 : "none",
                    overflow: isPreview ? "hidden" : "visible",
                    textOverflow: isPreview ? "ellipsis" : "clip",
                    wordBreak: "break-word"
                }}
                ref={textPreviewRef}
            >
                {children}
            </p>
            {showButton && refYOverflowing && isPreview && (
                <div
                    className="caption"
                    style={{ cursor: "pointer", marginTop: 8 }}
                    onClick={() => {
                        setIsPreview(false);
                    }}
                >
                    {t("screen.applicantNotes.readMore")}
                </div>
            )}
        </div>
    );
};

export const ApplicantPicture = styled.div`
    min-width: 24px;
    width: 24px;
    height: 24px;
    border-radius: 50%;

    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
`;

type NoApplicantPictureProps = {
    children?: React.ReactNode;
    style?: React.CSSProperties;
};

export const NoApplicantPicture = observer(({ children, style }: NoApplicantPictureProps) => {
    return (
        <div
            style={{
                minWidth: 24,
                width: 24,
                height: 24,
                borderRadius: "50%",
                fontSize: 8,
                textTransform: "uppercase",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor:
                    THEME.THEME_NAME === "dal" ? THEME.customColors.white : THEME.customColors.darkTertiary,
                border: THEME.THEME_NAME === "dal" ? `1px solid ${THEME.customColors.borderColor}` : "none",
                color: THEME.customColors.body1Dark,
                ...style
            }}
        >
            {children}
        </div>
    );
});

type DialogHeaderProps = { label: string; onClose: () => void };

export const DialogHeader = ({ label, onClose }: DialogHeaderProps) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            padding: 26,
            justifyContent: "space-between",
            width: "100%"
        }}
        className="h3"
    >
        <div style={{ flexGrow: 1 }}>
            {/* This empty div equals the width taken up by the Icon on the other side to perfectly middle the label */}
            <div style={{ width: 24 }} />
        </div>
        {label}
        <div style={{ display: "flex", flexGrow: 1, justifyContent: "flex-end" }}>
            <img src={THEME.Images.icClose} alt="" style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
    </div>
);

type DialogHeaderPageCountProps = {
    pageCount: number;
    pages: number;
    onClose: () => void;
    status?: IApplicationStateEnum;
};

export const DialogHeaderPageCount = ({ pageCount, pages, onClose, status }: DialogHeaderPageCountProps) => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 20,
            gap: 18
        }}
    >
        <div
            style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                color: THEME.customColors.primary
            }}
            className="body2semibold"
        >
            <div style={{ flexGrow: 1 }}>
                {/* This empty div equals the width taken up by the Icon on the other side to perfectly middle the label */}
                <div style={{ width: 24 }} />
            </div>
            {pageCount}/{pages}
            <div style={{ display: "flex", flexGrow: 1, justifyContent: "flex-end" }}>
                <img src={THEME.Images.icClose} alt="" style={{ cursor: "pointer" }} onClick={onClose} />
            </div>
        </div>
        <h3 style={{ textAlign: "center" }}>
            {t("screen.dashboard.changeStatus", {
                status: optionToString(allStatusOptions, status)
            })}
        </h3>
    </div>
);

type HrBotGreyButtonSimpleProps = {
    descriptionText: string;
    value: Option<any>;
    options: Array<Option<any>>;
    onSelect: (option: Option<any>) => void;
};

export const HrBotGreyButtonSimple = ({ descriptionText, value, options, onSelect }: HrBotGreyButtonSimpleProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = (option: Option<any>) => {
        onSelect(option);
        setAnchorEl(null);
    };

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 16 }} className="caption">
            {descriptionText}
            <div
                onClick={handleOpen}
                style={{
                    height: 40,
                    width: 200,
                    border: `1px solid ${THEME.customColors.dataUploadBorderColor}`,
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingLeft: 11
                }}
            >
                {t(value.messageId)}
                <div
                    style={{
                        height: 40,
                        width: 40,
                        borderLeft: `1px solid ${THEME.customColors.dataUploadBorderColor}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <img src={THEME.Images.icDropdownGrey} alt="" />
                </div>
            </div>
            <Menu id="job-category-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
                {options.map((option, index) => (
                    <MenuItem onClick={() => handleSelect(option)} key={index}>
                        {t(option.messageId)}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

type HrBotGreyButtonTogglesProps = {
    descriptionText: string;
    onClose: () => void;
    options: Array<Option<any>>;
    select: TableSelection;
    sorted?: boolean;
};

export const HrBotGreyButtonToggles = ({
    descriptionText,
    onClose,
    options,
    select,
    sorted
}: HrBotGreyButtonTogglesProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleOpenMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        onClose();
        setAnchorEl(null);
    };

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 16 }} className="caption">
            {descriptionText}
            <div
                onClick={handleOpenMenu}
                style={{
                    height: 40,
                    width: 200,
                    border: `1px solid ${THEME.customColors.dataUploadBorderColor}`,
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingLeft: 11
                }}
            >
                <div
                    style={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap"
                    }}
                >
                    {select.allSelected
                        ? t("screen.dashboard.all", { total: options.length })
                        : select.selectedIds.map((id) => optionToString(options, id)).join(", ")}
                </div>
                <div
                    style={{
                        height: 40,
                        minWidth: 40,
                        borderLeft: `1px solid ${THEME.customColors.dataUploadBorderColor}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <img src={THEME.Images.icDropdownGrey} alt="" />
                </div>
            </div>
            <Menu
                id="job-category-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                disableAutoFocusItem
            >
                <MenuItem
                    style={{
                        justifyContent: "space-between",
                        gap: 20,
                        backgroundColor: "transparent",
                        cursor: "default"
                    }}
                    disableRipple
                >
                    {t("screen.dashboard.all", { total: options.length })}
                    <CustomCheckbox
                        onChange={select.toggleSelectAll}
                        checked={select.allSelected}
                        indeterminate={select.someSelected}
                        style={select.someSelected ? { color: THEME.customColors.primary } : undefined}
                    />
                </MenuItem>
                {(sorted ? getSortedTranslatedOptions(options) : getTranslatedOptions(options)).map((option, index) => (
                    <MenuItem
                        key={index}
                        style={{
                            justifyContent: "space-between",
                            gap: 20,
                            backgroundColor: "transparent",
                            cursor: "default"
                        }}
                        disableRipple
                    >
                        {option.label}
                        <CustomCheckbox
                            onChange={() => {
                                select.toggleSelection(option.value);
                            }}
                            checked={select.isSelected(option.value)}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

type DivisionTagProps = { division: JobCategoryEnum };

export const DivisionTag = ({ division }: DivisionTagProps) => (
    <div
        style={{
            padding: "5px 8px 5px 5px",
            borderRadius: "100px",
            width: "max-content",
            backgroundColor: jobCategoryToColors(division).shade1,
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            fontSize: 9,
            lineHeight: "7px",
            color: jobCategoryToColors(division).shade2
        }}
        className="body1"
    >
        <div
            style={{
                backgroundColor: jobCategoryToColors(division).primary,
                width: 10,
                height: 10,
                borderRadius: "50%",
                marginRight: 4
            }}
        />
        {optionToString(getJobCategoryOptions(), division)}
    </div>
);

type HistoryLineProps = {
    history: GetApplicantDetailsResponseHistory;
    name: string;
    applicantPicture: React.ReactNode;
};

export const HistoryLine = ({ history, name, applicantPicture }: HistoryLineProps) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 0",
            wordBreak: "break-all",
            gap: 8
        }}
        className="body1 historyLine"
    >
        {applicantPicture}
        {`${moment(history.transitionDate).format(t("screen.dateTimeFormat"))} - ${historyToString(history, name)}`}
        {!history.emailSentExternal && history.toApplicationState !== "application_received" && (
            <img src={THEME.Images.icNoMessage} alt="" style={{ height: 16, width: 16 }} />
        )}
    </div>
);

type HrBotOrangeMultipleButtonProps = {
    label?: string;
    options: Option<any>[];
    onSelect: (option: any) => void;
    dropdown?: boolean;
};

export const HrBotOrangeMultipleButton = ({ label, options, onSelect, dropdown }: HrBotOrangeMultipleButtonProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleOpenMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSelectItem = (option: any) => {
        onSelect(option);
        setAnchorEl(null);
    };

    return (
        <>
            <div
                style={{
                    height: 40,
                    width: 200,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingLeft: 11
                }}
                className="caption hrBotMultipleButton"
            >
                {label}
                <div
                    onClick={handleOpenMenu}
                    style={{
                        height: 40,
                        width: 40,
                        borderRadius: "0 4px 4px 0",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: THEME.THEME_NAME === "dal" ? "transparent" : THEME.customColors.primaryDark,
                        borderLeft: THEME.THEME_NAME === "dal" ? `1px solid ${THEME.customColors.primary}` : "none"
                    }}
                >
                    <img src={dropdown ? THEME.Images.icChevronDownWhite : THEME.Images.icChevronRightWhite} alt="" />
                </div>
            </div>
            <Menu
                id="job-category-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
            >
                {options.map((status, index) => (
                    <MenuItem onClick={() => handleSelectItem(status.value)} key={index}>
                        {t(status.messageId)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

type GridItemProps = {
    children?: React.ReactNode;
};

export const TwoFieldGridItem = ({ children }: GridItemProps) => (
    <Grid item xs={12} sm={12} md={6} lg={6}>
        {children}
    </Grid>
);

export const ThreeFieldGridItem = ({ children }: GridItemProps) => (
    <Grid item xs={12} sm={12} md={6} lg={4}>
        {children}
    </Grid>
);

type ImageLogoProps = { onClick?: () => void; style?: React.CSSProperties };

export const ImageLogo = ({ onClick, style }: ImageLogoProps) => (
    <img
        alt="Logo"
        src={THEME.Images.logoImage}
        style={style}
        // onClick={() => THEME.toggleBranding()} // disabled with HRB-271
        onClick={onClick}
    />
);

type EvidenceTagProps = {
    children?: React.ReactNode;
};

export const EvidenceTag = observer(({ children }: EvidenceTagProps) => (
    <div
        style={{
            backgroundColor: THEME.customColors.evidenceBackground,
            borderRadius: 100,
            fontWeight: 500,
            fontSize: 9,
            lineHeight: "20px",
            display: "flex",
            justifyContent: "center",
            padding: "0 10px",
            width: "max-content",
            color: THEME.customColors.evidenceForeground
        }}
    >
        {children}
    </div>
));

type StyledBurgerProps = {
    open: boolean;
    colors: CustomColors;
};

const StyledBurger = styled.button<StyledBurgerProps>`
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    width: 32px;
    height: 32px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    z-index: 10;

    &:focus {
        outline: none;
    }

    div {
        width: 32px;
        height: 3px;
        background: ${({ colors }) => colors.primary};
        border-radius: 10px;
        transition: all 0.3s linear;
        position: relative;
        transform-origin: 1px;

        :first-child {
            transform: ${({ open }) => (open ? "rotate(45deg)" : "rotate(0)")};
        }

        :nth-child(2) {
            opacity: ${({ open }) => (open ? "0" : "1")};
            transform: ${({ open }) => (open ? "translateX(20px)" : "translateX(0)")};
        }

        :nth-child(3) {
            transform: ${({ open }) => (open ? "rotate(-45deg)" : "rotate(0)")};
        }
    }
`;

type BurgerProps = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Burger = observer(({ open, setOpen }: BurgerProps) => (
    <StyledBurger open={open} onClick={() => setOpen(!open)} colors={THEME.customColors}>
        <div />
        <div />
        <div />
    </StyledBurger>
));

type StyledBurgerMenuProps = {
    open: boolean;
    colors: CustomColors;
};

export const StyledBurgerMenu = styled.nav<StyledBurgerMenuProps>`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    background: ${({ colors }) => colors.darkPrimary};
    transform: ${({ open }) => (open ? "translateY(0)" : "translateY(-100%)")};
    height: 100vh;
    text-align: center;
    z-index: 9;
    padding-top: ${NAVBAR_HEIGHT}px;
    position: absolute;
    top: 0;
    left: 0;
    transition: transform 0.3s ease-in-out;
    width: 100%;
`;

type HrBotDialogProps = {
    children: React.ReactNode;
    open: boolean;
    maxWidth?: false | "xs" | "sm" | "md" | "lg" | "xl";
    onClose?: () => void;
};

export const HrBotDialog = ({ children, open, maxWidth, onClose }: HrBotDialogProps) => {
    const isMobile = useMobileMediaQuery();

    // TODO: check button positioning
    // TODO: double check all dialogs in case I missed some

    return (
        <Dialog
            open={open}
            maxWidth={maxWidth}
            onClose={onClose}
            PaperProps={{ className: "blackDalBorder", style: isMobile ? { width: "100% " } : {} }}
            fullScreen={isMobile}
        >
            {children}
        </Dialog>
    );
};

type HrBotOrangeButtonProps = {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    style?: React.CSSProperties;
};

export const HrBotOrangeButton = ({ label, onClick, disabled, style }: HrBotOrangeButtonProps) => (
    <div
        style={{
            height: 40,
            width: 200,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: disabled ? "default" : "pointer",
            backgroundColor: disabled ? THEME.customColors.disabledElements : THEME.customColors.primary,
            color: THEME.customColors.white,
            paddingLeft: 11,
            ...style
        }}
        className={`caption${disabled ? " disabled" : ""}`}
        onClick={!disabled ? onClick : undefined}
    >
        {label}
    </div>
);

type ApplicationDateChipProps = {
    date?: GetApplicantsObjectInterviewDates;
    onClick?: () => void;
    style?: React.CSSProperties;
};

export const ApplicationDateChip = ({ date, onClick, style }: ApplicationDateChipProps) => (
    <div
        style={{
            height: 20,
            width: 50,
            fontSize: 9,
            lineHeight: "20px",
            borderRadius: 4,
            display: "flex",
            justifyContent: "center",
            ...style
        }}
        className="applicationDateChip"
        onClick={() => {
            onClick?.();
        }}
    >
        {moment(date?.startTime).format(t("screen.dateFormat"))}
    </div>
);

interface HrBotSwitchStyles extends Partial<Record<SwitchClassKey, string>> {
    focusVisible?: string;
}

interface HrBotSwitchProps extends SwitchProps {
    classes: HrBotSwitchStyles;
}

export const HrBotSwitch = withStyles((theme: Theme) =>
    createStyles({
        root: {
            width: 48,
            height: 24,
            padding: 0
        },
        switchBase: {
            padding: 2,
            "&$checked": {
                transform: "translateX(24px)",
                color: theme.palette.common.white,
                "& + $track": {
                    backgroundColor: THEME.customColors.primary,
                    opacity: 0.6,
                    border: "none"
                }
            },
            "&$focusVisible $thumb": {
                color: "#ED7226"
            }
        },
        thumb: {
            width: 20,
            height: 20
        },
        track: {
            borderRadius: 24 / 2,
            backgroundColor: "#ededed",
            opacity: 1,
            transition: theme.transitions.create(["background-color", "border"])
        },
        checked: {},
        focusVisible: {}
    })
)(({ classes, ...props }: HrBotSwitchProps) => {
    return (
        <Switch
            focusVisibleClassName={classes.focusVisible}
            disableRipple
            classes={{
                root: classes.root,
                switchBase: classes.switchBase,
                thumb: classes.thumb,
                track: classes.track,
                checked: classes.checked
            }}
            {...props}
        />
    );
});

type AuthorFieldProps = {
    type: "created" | "updated";
    user?: User;
    date?: Date | Moment;
};

export const AuthorField = ({ type, user, date }: AuthorFieldProps) => {
    if (user) {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexGrow: 1 }} className="caption">
                <div style={{ flex: 1 }}>
                    {type === "created" ? t("screen.applicantNotes.createdBy") : t("screen.applicantNotes.updatedBy")}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 3 }}>
                    {user.pictureUrl ? (
                        <ApplicantPicture style={{ backgroundImage: `url(${user.pictureUrl})` }} />
                    ) : (
                        <NoApplicantPicture>
                            {user.firstName && user.firstName[0]}
                            {user.lastName && user.lastName[0]}
                            {user.lastName && user.lastName[0]}
                        </NoApplicantPicture>
                    )}
                    <div>{`${user.firstName} ${user.lastName}`}</div>
                    {moment(date).format(t("screen.dateTimeFormat"))}
                </div>
            </div>
        );
    } else {
        return <></>;
    }
};

type NoteMentionsProps = {
    mentions?: string[];
};

export const NoteMentions = ({ mentions }: NoteMentionsProps) => {
    if (mentions) {
        return (
            <div style={{ display: "flex", alignItems: "flex-start" }} className="caption">
                <div style={{ flex: 1 }}>{t("screen.applicantHistory.notified")}</div>
                <div style={{ flex: 3 }}>{mentions.join(", ")}</div>
            </div>
        );
    } else {
        return <></>;
    }
};

export const CommentsCointainer = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

type CommentsToggleProps = {
    setShowComments: React.Dispatch<React.SetStateAction<boolean>>;
    showComments: boolean;
    comments: NoteComment[] | undefined;
};

export const CommentsToggle = ({ setShowComments, showComments, comments }: CommentsToggleProps) => {
    if (comments) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    width: "max-content"
                }}
                className="caption"
                onClick={() => setShowComments(!showComments)}
            >
                {showComments ? (
                    <img src={THEME.Images.icChevronUp} alt="" />
                ) : (
                    <img src={THEME.Images.icChevronDown} alt="" />
                )}
                {`${comments.length} ${comments.length > 1 ? t("screen.applicantNotes.comments") : t("screen.applicantNotes.comment")
                    }`}
            </div>
        );
    } else {
        return <></>;
    }
};
